import React, { useState, useEffect, useRef } from 'react';
import { CheckedIconWithoutBg, SquareDownArrow, SquareUpArrow, WarningGreyIcon } from 'assests/GmsIcons';

type TCommonAttributesProps = {
    recategorizationResponse: RecategorizationResponse | undefined
    payload: TRecategorizePayload
    setPayload: React.Dispatch<React.SetStateAction<TRecategorizePayload>>
}

const CommonAttributeTable = ({ recategorizationResponse, payload, setPayload }: TCommonAttributesProps) => {
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set([]))
    const headerCheckboxRef = useRef<HTMLInputElement>(null)

    const getDataFromPayload = (): TAttributeRow[] => {
        if (!recategorizationResponse?.common_attr_list || !payload.common_attr_list) {
            return []
        }

        return recategorizationResponse.common_attr_list.map((attr) => {
            const payloadAttr = payload.common_attr_list.find(
                (p) => p.attribute_id === attr.attribute_id
            )

            return {
                id: attr.attribute_id,
                sourceCategory: attr.attribute_name,
                targetCategory: attr.attribute_name,
                isChecked: payloadAttr?.checked ?? true,
                isExpandable: true,
                commonValues: attr.attribute_value?.map((val) => val.value) || [],
            }
        })
    }

    const [tableData, setTableData] = useState<TAttributeRow[]>(getDataFromPayload())

    useEffect(() => {
        setTableData(getDataFromPayload())
    }, [payload, recategorizationResponse])

    const checkedRows = new Set(
        tableData.filter((row) => row.isChecked).map((row) => row.id)
    )

    const isAllChecked = checkedRows.size === tableData.length && tableData.length > 0

    useEffect(() => {
        if (headerCheckboxRef.current) {
            headerCheckboxRef.current.checked = isAllChecked
            headerCheckboxRef.current.indeterminate =
                checkedRows.size > 0 && checkedRows.size < tableData.length
        }
    }, [checkedRows.size, tableData.length, isAllChecked])

    const toggleExpand = (id: string) => {
        setExpandedRows((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(id)) {
                newSet.delete(id)
            } else {
                newSet.add(id)
            }
            return newSet
        })
    }

    const updatePayload = (updatedRows: TAttributeRow[]) => {
        const updatedAttrList = updatedRows.map((row) => ({
            attribute_id: row.id,
            attribute_name: row.sourceCategory,
            checked: row.isChecked,
        }))

        setPayload((prev) => ({
            ...prev,
            common_attr_list: updatedAttrList,
        }))
    }

    const toggleCheck = (id: string) => {
        const updatedData = tableData.map((row) =>
            row.id === id ? { ...row, isChecked: !row.isChecked } : row
        )
        setTableData(updatedData)
        updatePayload(updatedData)
    }

    const toggleAll = () => {
        const newCheckedState = !isAllChecked
        const updatedData = tableData.map((row) => ({
            ...row,
            isChecked: newCheckedState,
        }))
        setTableData(updatedData)
        updatePayload(updatedData)
    }

    return (
        <div className="tw-w-full tw-max-w-4xl tw-mx-auto tw-bg-white tw-h-auto tw-overflow-y-auto">
            <div className="tw-flex tw-flex-col">
                <table className="tw-w-full">
                    <thead className="tw-bg-[#F4F5F7] tw-rounded-t-lg">
                        <tr className="tw-border-[1px] tw-border-[#DADBDD] tw-rounded-t-lg">
                            <th className="tw-w-12 tw-pl-4 tw-py-3 tw-rounded-tl-lg">
                                <label className="tw-relative tw-flex tw-items-center tw-justify-center">
                                    <input
                                        type="checkbox"
                                        ref={headerCheckboxRef}
                                        checked={isAllChecked}
                                        onChange={toggleAll}
                                        className="tw-hidden"
                                    />
                                    <div
                                        className={`tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center tw-border-2 tw-rounded-[4px] tw-cursor-pointer tw-transition-all tw-duration-200 ${isAllChecked
                                            ? 'tw-bg-[#091CAA] tw-border-[#091CAA]'
                                            : 'tw-border-[#091CAA] hover:tw-border-[#091CAA]'
                                            }`}
                                    >
                                        {isAllChecked && <CheckedIconWithoutBg />}
                                    </div>
                                </label>
                            </th>
                            <th className="tw-py-3 tw-text-left tw-font-semibold tw-text-sm tw-text-[#6D788C]">
                                {payload?.source_category_name ?? ''}
                            </th>
                            <th className="tw-py-3 tw-text-left tw-font-semibold tw-text-sm tw-text-[#6D788C]">
                                {payload?.target_category_name ?? ''}
                            </th>
                            <th className="tw-w-12 tw-pr-4"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {tableData.map((row, index) => (
                            <React.Fragment key={row.id}>
                                <tr
                                    className={`tw-border tw-border-[#DADBDD] ${index % 2 === 0 ? 'tw-bg-white' : 'tw-bg-[#F4F5F7]'
                                        }`}
                                >
                                    <td className="tw-w-12 tw-pl-4 tw-py-4">
                                        <label className="tw-relative tw-flex tw-items-center tw-justify-center">
                                            <input
                                                type="checkbox"
                                                checked={row.isChecked}
                                                onChange={() => toggleCheck(row.id)}
                                                className="tw-hidden"
                                            />
                                            <div
                                                className={`tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center tw-border-2 tw-rounded-[4px] tw-cursor-pointer tw-transition-all tw-duration-200 ${row.isChecked
                                                    ? 'tw-bg-[#091CAA] tw-border-[#091CAA]'
                                                    : 'tw-border-[#091CAA] hover:tw-border-[#091CAA]'
                                                    }`}
                                            >
                                                {row.isChecked && <CheckedIconWithoutBg />}
                                            </div>
                                        </label>
                                    </td>
                                    <td className="tw-py-4 tw-text-[#172B4D] tw-text-sm">
                                        {row.sourceCategory}
                                    </td>
                                    <td className="tw-py-4 tw-text-[#172B4D] tw-text-sm">
                                        {row.targetCategory}
                                    </td>
                                    {row.isExpandable && (
                                        <td className="tw-w-12 tw-pr-4">
                                            <button
                                                onClick={() => toggleExpand(row.id)}
                                                className="tw-bg-[#F4F5F7] tw-rounded hover:tw-bg-gray-100"
                                            >
                                                {expandedRows.has(row.id) ? (
                                                    <SquareUpArrow />
                                                ) : (
                                                    <SquareDownArrow />
                                                )}
                                            </button>
                                        </td>
                                    )}
                                </tr>
                                {expandedRows.has(row.id) && (
                                    <tr className="tw-border tw-border-[#DADBDD]">
                                        <td colSpan={4} className="tw-px-6 tw-py-2">
                                            {row.commonValues?.length ? (
                                                <div>
                                                    <p className="tw-text-[#97A0AF] tw-font-semibold tw-text-xs">
                                                        Common Attribute Values
                                                    </p>
                                                    <div className="tw-flex tw-flex-wrap tw-gap-4 tw-mt-2">
                                                        {row.commonValues.map((value) => (
                                                            <span
                                                                key={value}
                                                                className="tw-px-2 tw-py-1 tw-bg-[#F4F5F7] tw-rounded-sm tw-text-xs tw-text-[#172B4D]"
                                                            >
                                                                {value}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="tw-text-gray-500">
                                                    No attribute values available
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
                {checkedRows.size < tableData.length && (
                    <div className="tw-border tw-border-[#DADBDD] tw-px-6 tw-py-4 tw-flex tw-items-center tw-gap-2 tw-text-[#6D788C] tw-bg-white">
                        <WarningGreyIcon />
                        <span>Attribute values for this attribute will be blank</span>
                    </div>
                )}
            </div>
        </div>
    )
}

export default CommonAttributeTable